import { create } from "zustand";
import type { CartItem } from "../../../hooks/stores/useCartStore";

/**
 * Transient state shared across the checkout stepper. Lives outside the
 * URL because the only thing the URL encodes is the *step* — selected
 * address ids and other in-flight choices belong here. It's in memory only:
 * a refresh drops them, and the checkout page sends a payment step without a
 * delivery address back to the delivery step.
 *
 * Cleared on successful payment (PaymentForm) and when the user returns
 * to the basket step after editing the cart.
 */
interface CheckoutState {
  /** Selected delivery address (Odoo res.partner id, type=delivery). */
  shippingAddressId: number | null;
  setShippingAddressId: (id: number | null) => void;

  /**
   * Whether the buyer wants the order billed to the same address it ships
   * to. When `true`, the FE doesn't send `billingAddressId` and Odoo bills
   * the company partner (the legal entity) by default. When `false`, the
   * UI surfaces the invoice address book and the chosen id is sent.
   */
  billingSameAsShipping: boolean;
  setBillingSameAsShipping: (value: boolean) => void;

  /** Selected billing address (Odoo res.partner id, type=invoice or other). */
  billingAddressId: number | null;
  setBillingAddressId: (id: number | null) => void;

  /**
   * Frozen cart snapshot captured when the buyer advances to payment. This
   * keeps the payment step and the persisted draft SO aligned even if the
   * live cart changes in another tab or the store rehydrates late.
   */
  checkoutItems: CartItem[] | null;
  setCheckoutItems: (items: CartItem[] | null) => void;

  /**
   * Loyalty points the buyer chose to redeem on this order. `null` means
   * "don't apply any". Forwarded to `salesService.createQuotation` so the
   * BE can attach a discount line to the Odoo SO and stage a pending
   * redemption (finalized on payment_intent.succeeded).
   */
  loyaltyPointsToRedeem: number | null;
  setLoyaltyPointsToRedeem: (points: number | null) => void;

  reset: () => void;
}

export const useCheckoutStore = create<CheckoutState>((set) => ({
  shippingAddressId: null,
  setShippingAddressId: (id) => set({ shippingAddressId: id }),

  billingSameAsShipping: true,
  setBillingSameAsShipping: (value) => set({ billingSameAsShipping: value }),

  billingAddressId: null,
  setBillingAddressId: (id) => set({ billingAddressId: id }),

  checkoutItems: null,
  setCheckoutItems: (items) => set({ checkoutItems: items }),

  loyaltyPointsToRedeem: null,
  setLoyaltyPointsToRedeem: (points) => set({ loyaltyPointsToRedeem: points }),

  reset: () =>
    set({
      shippingAddressId: null,
      billingSameAsShipping: true,
      billingAddressId: null,
      checkoutItems: null,
      loyaltyPointsToRedeem: null,
    }),
}));
